import { SESSION_DEADLINE_MS, withDeadline } from "./apiRetry.js";

// The Cognito session reader both apps share. 07/10/2026: a cold start with an
// expired token sent 4 REFRESH_TOKEN_AUTH in one second, then nothing reached
// the API for ~100 s. The SDK refreshes inside getSession() and every
// getCurrentUser() is a new object, so the sharing has to live here.

/** `unknown` is a timeout or a network failure: NOT signed out. */
export type SessionRead<S> =
  | { kind: "session"; session: S }
  | { kind: "none" }
  | { kind: "unknown" };

export type KnownSessionRead<S> = Exclude<SessionRead<S>, { kind: "unknown" }>;

/** The token step gave no answer in time, or the network failed under it. */
export class SessionUnknownError extends Error {
  constructor() {
    super("auth_timeout");
    this.name = "SessionUnknownError";
  }
}

export type SessionLike = {
  isValid(): boolean;
  getIdToken(): { getExpiration(): number };
  getRefreshToken(): unknown;
  // Device clock minus Cognito's, in s. At runtime, not in the SDK typings.
  getClockDrift?(): number;
};

/** The two calls the reader makes on a Cognito user. */
export type SessionUser<S extends SessionLike> = {
  getSession(cb: (err: Error | null, session: S | null) => void): void;
  refreshSession(
    token: ReturnType<S["getRefreshToken"]>,
    cb: (err: unknown, session: S | null) => void,
  ): void;
};

export type SessionStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
};

/** `base`, with every write dropped once `writable()` is false. */
export const gateSessionStorage = (
  base: SessionStorage,
  writable: () => boolean,
): SessionStorage => ({
  getItem: (key) => base.getItem(key),
  setItem: (key, value) => {
    if (writable()) base.setItem(key, value);
  },
  removeItem: (key) => {
    if (writable()) base.removeItem(key);
  },
  clear: () => {
    if (writable()) base.clear();
  },
});

export type SessionReaderConfig<S extends SessionLike> = {
  /** The stored user, writing through storage gated by `writable`, or null. */
  getCurrentUser: (writable: () => boolean) => SessionUser<S> | null;
  /** The backend part of the epoch. The account part is `reset()`. */
  epoch: () => string;
  /** A token closer than this to expiry is refreshed before it is handed out. */
  refreshSlackSeconds: number;
  /** After a refresh that wrote its tokens. Best effort: a rejection is
   *  ignored (the fresh token is in memory and the next write flushes
   *  again), so the app logs it inside the hook. */
  afterRefresh?: () => Promise<void>;
};

export type SessionReader<S> = {
  /** Boot, email, sub: the stored session, refreshed by the SDK if expired.
   *  Unknown when the epoch changes before the answer. */
  read(): Promise<SessionRead<S>>;
  /** apiFetch: read plus any refresh under one deadline. Null when signed
   *  out; rejects with SessionUnknownError when the answer is unknown or
   *  the epoch changed before it. */
  token(): Promise<S | null>;
  /** Sign-in and sign-out: every caller still waiting gets unknown now. */
  reset(): void;
};

const NONE = { kind: "none" } as const;
const UNKNOWN = { kind: "unknown" } as const;

// Unknown is a failure that heals by itself: the network (the SDK's
// "NetworkError" when fetch threw, a bare TypeError for a reply it could not
// read), or Cognito saying "not now" (throttling, its own fault, a 5xx: the
// SDK sets statusCode only when the code comes from the response header).
// Every other coded error is signed out, as before 07/10: a refused token, a
// deleted user, a pool or client that is gone never heals, and asking again
// would hold the splash for ever. The SDK's "nothing stored" errors carry no code.
const TRANSIENT = new Set([
  "NetworkError",
  "TooManyRequestsException",
  "InternalErrorException",
  "LimitExceededException",
  "ServiceUnavailable",
  "ServiceUnavailableException",
]);
const outcomeOf = (err: unknown): SessionRead<never> => {
  if (err instanceof TypeError) return UNKNOWN;
  const { code, statusCode } = (err ?? {}) as { code?: unknown; statusCode?: unknown };
  if (typeof code === "string" && TRANSIENT.has(code)) return UNKNOWN;
  return typeof statusCode === "number" && statusCode >= 500 ? UNKNOWN : NONE;
};

// How long a flight may hold its slot. The SDK retries a failed refresh by
// itself (Client.js requestWithRetry: 6 tries, about 6 s of backoff, on any
// error), and while it does nobody may start another refreshSession: that
// was the 07/10 burst. Callers stop waiting at SESSION_DEADLINE_MS; only an
// SDK call that never answers is abandoned, here.
const FLIGHT_RELEASE_MS = 60_000;

type Work<S> = (writable: () => boolean) => Promise<SessionRead<S>>;
type Flight<S> = { epoch: string; done: Promise<SessionRead<S>> };
type Slot = "read" | "refresh";
// `meanwhile` holds a session read while the refresh after it runs.
type Pass<S> = (
  epoch: string,
  meanwhile: (read: SessionRead<S>) => void,
) => Promise<SessionRead<S>>;

export function createSessionReader<S extends SessionLike>(
  cfg: SessionReaderConfig<S>,
): SessionReader<S> {
  let generation = 0;
  const epochNow = () => `${cfg.epoch()}#${generation}`;
  // One flight per slot and epoch. Read and refresh never share one: a
  // caller that needs a fresh token must not get the expiring one a plain
  // read returns.
  const slots: Record<Slot, Flight<S> | null> = { read: null, refresh: null };
  // The callers still waiting: reset() answers them unknown at once.
  const waiting = new Set<() => void>();

  const fly = (slot: Slot, epoch: string, work: Work<S>): Promise<SessionRead<S>> => {
    const running = slots[slot];
    if (running && running.epoch === epoch) return running.done;
    // Late SDK writes from an old epoch are dropped through this gate.
    const answer = withDeadline(
      work(() => epochNow() === epoch),
      () => undefined,
      FLIGHT_RELEASE_MS,
      () => new SessionUnknownError(),
    ).catch((): SessionRead<S> => UNKNOWN);
    const flight: Flight<S> = {
      epoch,
      done: answer.then((read) => {
        if (slots[slot] === flight) slots[slot] = null;
        return read;
      }),
    };
    slots[slot] = flight;
    return flight.done;
  };

  // getSession refreshes an EXPIRED token by itself. A read that starts while
  // a refresh of its epoch runs waits for it: storage then holds the new
  // tokens and the SDK has nothing to refresh, so one refreshSession flies.
  const readWork =
    (epoch: string): Work<S> =>
    async (writable) => {
      const refreshing = slots.refresh;
      if (refreshing && refreshing.epoch === epoch) await refreshing.done;
      // A read of a gone epoch never touches the pool of the new one.
      if (!writable()) return UNKNOWN;
      return new Promise((resolve) => {
        const user = cfg.getCurrentUser(writable);
        if (!user) return resolve(NONE);
        user.getSession((err, session) => {
          if (err) return resolve(outcomeOf(err));
          resolve(session?.isValid() ? { kind: "session", session } : NONE);
        });
      });
    };

  const refreshWork =
    (stale: S): Work<S> =>
    async (writable) => {
      if (!writable()) return UNKNOWN;
      const read = await new Promise<SessionRead<S>>((resolve) => {
        const user = cfg.getCurrentUser(writable);
        if (!user) return resolve(NONE);
        const token = stale.getRefreshToken() as ReturnType<S["getRefreshToken"]>;
        user.refreshSession(token, (err, session) => {
          if (err) return resolve(outcomeOf(err));
          resolve(session ? { kind: "session", session } : NONE);
        });
      });
      if (read.kind === "session" && writable() && cfg.afterRefresh) {
        // A flush that fails does not unmake the refresh (persistSession).
        await cfg.afterRefresh().catch(() => undefined);
      }
      return read;
    };

  // On Cognito's clock, as isValid() reads it: a device clock running ahead
  // must not refresh on every call.
  const nearExpiry = (session: S) => {
    const now = Math.floor(Date.now() / 1000) - (session.getClockDrift?.() ?? 0);
    return session.getIdToken().getExpiration() - now < cfg.refreshSlackSeconds;
  };

  const readPass: Pass<S> = (epoch) => fly("read", epoch, readWork(epoch));

  // Read, then refresh if needed, both in ONE epoch: the refresh starts from
  // the session that epoch read, never from an older account's.
  const freshPass: Pass<S> = async (epoch, meanwhile) => {
    const read = await readPass(epoch, meanwhile);
    if (read.kind !== "session" || !nearExpiry(read.session)) return read;
    if (epochNow() !== epoch) return UNKNOWN;
    meanwhile(read);
    const fresh = await fly("refresh", epoch, refreshWork(read.session));
    // The early refresh is a margin: a token still valid serves without it,
    // here and at the deadline (answer).
    return fresh.kind === "unknown" && read.session.isValid() ? read : fresh;
  };

  // A caller is answered in the epoch it started in, or unknown: an answer
  // from another epoch is another account's (sign-out, sign-in, backend
  // switch). One deadline covers the read and any refresh.
  const answer = async (pass: Pass<S>): Promise<SessionRead<S>> => {
    const epoch = epochNow();
    let held = UNKNOWN as SessionRead<S>;
    let outlive = (): void => undefined;
    const outlived = new Promise<SessionRead<S>>((resolve) => {
      outlive = () => resolve(UNKNOWN);
    });
    waiting.add(outlive);
    try {
      const read = await withDeadline(
        Promise.race([pass(epoch, (seen) => (held = seen)), outlived]),
        () => undefined,
        SESSION_DEADLINE_MS,
        () => new SessionUnknownError(),
      );
      return epochNow() === epoch ? read : UNKNOWN;
    } catch {
      const valid = held.kind === "session" && held.session.isValid();
      return valid && epochNow() === epoch ? held : UNKNOWN;
    } finally {
      waiting.delete(outlive);
    }
  };

  return {
    read: () => answer(readPass),
    token: async () => {
      const read = await answer(freshPass);
      if (read.kind === "unknown") throw new SessionUnknownError();
      return read.kind === "session" ? read.session : null;
    },
    reset: () => {
      generation += 1;
      slots.read = null;
      slots.refresh = null;
      for (const outlive of waiting) outlive();
      waiting.clear();
    },
  };
}

/** apiFetch's map, both apps: an unknown session is status 0 `auth_timeout`,
 *  like `api_timeout`, never a 401 (nobody said the account is gone). Any
 *  other error passes through. Each app owns its ApiError, so it builds it. */
export const authErrorToApi = (
  err: unknown,
  apiError: (status: number, code: string) => Error,
): unknown => (err instanceof SessionUnknownError ? apiError(0, "auth_timeout") : err);

/** The reader's `getCurrentUser`, both apps: a NEW user for the stored
 *  username, writing through `storage` gated by `writable`. */
export const makeGatedUser =
  <P extends { getCurrentUser(): { getUsername(): string } | null }, U>(
    User: new (data: { Username: string; Pool: P; Storage: SessionStorage }) => U,
    pool: () => P,
    storage: SessionStorage,
  ) =>
  (writable: () => boolean): U | null => {
    const current = pool();
    const stored = current.getCurrentUser();
    if (!stored) return null;
    return new User({
      Username: stored.getUsername(),
      Pool: current,
      Storage: gateSessionStorage(storage, writable),
    });
  };

/** The payload of the id token stored for `username`, expired or not, read
 *  with no network: who this device worked for last. Null when there is no
 *  user, no token, or it does not decode. `decode` is the SDK's
 *  (CognitoIdToken.decodePayload): this package has no base64. */
export const storedIdTokenPayload = (
  storage: Pick<SessionStorage, "getItem">,
  clientId: string,
  username: string | null,
  decode: (jwt: string) => Record<string, unknown>,
): Record<string, unknown> | null => {
  if (username === null) return null;
  const jwt = storage.getItem(
    `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`,
  );
  if (!jwt) return null;
  try {
    return decode(jwt);
  } catch {
    return null;
  }
};
